<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-violet-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-violet-600 tracking-widest">Taller · Semanal</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Reporte Técnico Semanal
          <span class="px-2 sm:px-3 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 text-xs sm:text-sm font-black rounded-lg border border-violet-200 dark:border-violet-800">
            {{ recepcionesCompletadas.length }}
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 font-medium mt-0.5">
          Reparaciones completadas (Reparado / Entregado) — Lunes a Sábado
        </p>
      </div>
      <!-- Selector de semana -->
      <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
        <button @click="semanaAnterior" class="p-2 sm:p-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm">
          <Icon icon="mdi:chevron-left" class="text-lg sm:text-xl text-gray-600 dark:text-gray-300" />
        </button>
        <div class="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-3 sm:px-5 py-2 sm:py-2.5 shadow-sm text-center min-w-[160px] sm:min-w-[200px]">
          <span class="text-[8px] sm:text-[10px] font-black text-violet-600 uppercase tracking-widest block">Semana</span>
          <span class="text-[10px] sm:text-sm font-black text-gray-900 dark:text-white">
            {{ formatFechaCorta(semanaActual.lunes) }} — {{ formatFechaCorta(semanaActual.sabado) }}
          </span>
        </div>
        <button @click="semanaSiguiente" :disabled="esSemanaActual" class="p-2 sm:p-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Icon icon="mdi:chevron-right" class="text-lg sm:text-xl text-gray-600 dark:text-gray-300" />
        </button>
        <button @click="irSemanaActual" class="px-3 sm:px-4 py-2 sm:py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-black text-[9px] sm:text-xs uppercase tracking-widest transition-all shadow-md">
          Esta semana
        </button>
      </div>
    </div>

    <!-- Filtro por técnico -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 shadow-sm p-3 sm:p-5 mb-4 sm:mb-6 animate-fadeInUp flex flex-wrap gap-3 sm:gap-4 items-end">
      <div class="flex-1 min-w-[160px] sm:min-w-[200px]">
        <label class="block text-[9px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1 sm:mb-2 ml-1">Técnico</label>
        <select
          v-model="filtroTecnico"
          class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl outline-none font-bold text-sm text-gray-900 dark:text-white"
        >
          <option value="todos">Todos los técnicos</option>
          <option v-for="tec in tecnicos" :key="tec.idTecnico" :value="tec.idTecnico">
            {{ tec.nombre }} {{ tec.apellido }}
          </option>
        </select>
      </div>
      <div class="w-24 sm:w-32">
        <label class="block text-[9px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1 sm:mb-2 ml-1">% Comisión</label>
        <div class="relative">
          <input
            v-model.number="porcentajeComision"
            type="number"
            min="0"
            max="100"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl outline-none font-bold text-sm text-gray-900 dark:text-white"
          />
          <span class="absolute right-3 top-1/2 -translate-y-1/2 font-black text-violet-500 text-[8px] sm:text-[10px]">%</span>
        </div>
      </div>
      <button
        @click="cargarDatos"
        class="px-3 sm:px-5 py-2.5 sm:py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-black text-[9px] sm:text-xs uppercase tracking-widest transition-all shadow-md flex items-center gap-1.5 sm:gap-2"
      >
        <Icon icon="mdi:refresh" :class="{'animate-spin': cargando}" class="text-base sm:text-lg" />
        Actualizar
      </button>
      <button
        @click="imprimirReporte"
        :disabled="!hayDatos"
        class="px-3 sm:px-5 py-2.5 sm:py-3 bg-gray-900 dark:bg-gray-700 hover:bg-black text-white rounded-xl font-black text-[9px] sm:text-xs uppercase tracking-widest transition-all shadow-md flex items-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Icon icon="mdi:printer" class="text-base sm:text-lg" />
        Imprimir
      </button>
    </div>

    <!-- Tarjetas de resumen global -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6 animate-fadeInUp">
      <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 sm:p-5">
        <div class="flex items-center justify-between mb-2 sm:mb-3">
          <div class="p-1.5 sm:p-2.5 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
            <Icon icon="mdi:check-circle" class="text-violet-600 dark:text-violet-400 text-lg sm:text-xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Total</span>
        </div>
        <p class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white">{{ resumenGlobal.totalReparaciones }}</p>
        <p class="text-[9px] sm:text-xs font-bold text-gray-500 mt-0.5 sm:mt-1">Completadas</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 sm:p-5 border-l-4 border-l-violet-500">
        <div class="flex items-center justify-between mb-2 sm:mb-3">
          <div class="p-1.5 sm:p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
            <Icon icon="mdi:account-group" class="text-emerald-600 dark:text-emerald-400 text-lg sm:text-xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Técnicos</span>
        </div>
        <p class="text-xl sm:text-3xl font-black text-emerald-600">{{ gruposPorTecnico.length }}</p>
        <p class="text-[9px] sm:text-xs font-bold text-gray-500 mt-0.5 sm:mt-1">Activos</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 sm:p-5 border-l-4 border-l-emerald-500">
        <div class="flex items-center justify-between mb-2 sm:mb-3">
          <div class="p-1.5 sm:p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
            <Icon icon="mdi:cash-multiple" class="text-emerald-600 text-lg sm:text-xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Total</span>
        </div>
        <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ resumenGlobal.montoTotal.toFixed(2) }}</p>
        <p class="text-[9px] sm:text-xs font-bold text-emerald-600 mt-0.5 sm:mt-1">Generado en USD</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 sm:p-5 border-l-4 border-l-violet-500">
        <div class="flex items-center justify-between mb-2 sm:mb-3">
          <div class="p-1.5 sm:p-2.5 bg-violet-50 dark:bg-violet-900/20 rounded-xl">
            <Icon icon="mdi:account-cash" class="text-violet-600 text-lg sm:text-xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Pagos</span>
        </div>
        <p class="text-lg sm:text-2xl font-black text-violet-600">${{ resumenGlobal.pagoTotalTecnicos.toFixed(2) }}</p>
        <p class="text-[9px] sm:text-xs font-bold text-gray-500 mt-0.5 sm:mt-1">A pagar a técnicos</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 sm:p-5 hidden md:block border-l-4 border-l-amber-500">
        <div class="flex items-center justify-between mb-2 sm:mb-3">
          <div class="p-1.5 sm:p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
            <Icon icon="mdi:percent" class="text-amber-600 text-lg sm:text-xl" />
          </div>
          <span class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Comisión</span>
        </div>
        <p class="text-xl sm:text-3xl font-black text-amber-600">{{ porcentajeComision }}%</p>
        <p class="text-[9px] sm:text-xs font-bold text-gray-500 mt-0.5 sm:mt-1">Promedio</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="cargando" class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 p-12 sm:p-16 text-center shadow-sm">
      <div class="inline-block w-8 h-8 sm:w-10 sm:h-10 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mb-3 sm:mb-4"></div>
      <p class="text-gray-500 font-bold text-sm sm:text-base">Cargando reparaciones completadas...</p>
    </div>

    <!-- Sin datos -->
    <div v-else-if="!hayDatos" class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 p-12 sm:p-16 text-center shadow-sm">
      <div class="flex flex-col items-center opacity-30">
        <Icon icon="mdi:check-circle-outline" class="text-6xl sm:text-8xl mb-3 sm:mb-4 text-gray-400" />
        <h4 class="text-xl sm:text-2xl font-black text-gray-500">Sin reparaciones completadas</h4>
        <p class="text-xs sm:text-sm font-bold text-gray-400 mt-1 sm:mt-2">
          No hay reparaciones en estado <span class="text-violet-600">REPARADO</span> o <span class="text-violet-600">ENTREGADO</span><br>
          del {{ formatFechaCorta(semanaActual.lunes) }} al {{ formatFechaCorta(semanaActual.sabado) }}
        </p>
      </div>
    </div>

    <!-- Grupos por técnico -->
    <div v-else class="space-y-4 sm:space-y-6 animate-fadeIn">
      <div
        v-for="grupo in gruposPorTecnico"
        :key="grupo.tecnico"
        class="bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
      >
        <!-- Cabecera del técnico -->
        <div class="bg-gradient-to-r from-violet-600 to-purple-700 px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-3 sm:gap-4">
            <div class="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-black text-base sm:text-xl shadow-inner">
              {{ iniciales(grupo.tecnico) }}
            </div>
            <div>
              <h3 class="text-white font-black text-base sm:text-lg">{{ grupo.tecnico }}</h3>
              <p class="text-violet-200 text-[9px] sm:text-xs font-bold uppercase tracking-widest">Técnico Reparador</p>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-3 sm:gap-6 text-white text-right">
            <div>
              <span class="text-[8px] sm:text-xs font-black uppercase tracking-widest text-violet-200 block mb-0.5 sm:mb-1">Monto Total</span>
              <span class="text-lg sm:text-2xl font-black block text-emerald-300">${{ grupo.totalMonto.toFixed(2) }}</span>
            </div>
            <div class="h-8 sm:h-10 w-[1px] bg-white/20 hidden xs:block"></div>
            <div>
              <span class="text-[8px] sm:text-xs font-black uppercase tracking-widest text-violet-200 block mb-0.5 sm:mb-1">Comisión %</span>
              <div class="relative inline-block w-14 sm:w-20">
                <input 
                  type="number" 
                  :value="grupo.porcentajeAplicado"
                  @input="e => $set(comisionesManuales, grupo.tecnico, parseFloat(e.target.value))"
                  class="w-full bg-white/20 border border-white/30 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-white font-black text-xs sm:text-sm outline-none focus:bg-white/30"
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <div class="h-8 sm:h-10 w-[1px] bg-white/20 hidden xs:block"></div>
            <div>
              <span class="text-[8px] sm:text-xs font-black uppercase tracking-widest text-violet-200 block mb-0.5 sm:mb-1">Pago Técnico</span>
              <span class="text-lg sm:text-2xl font-black block text-white shadow-sm">${{ grupo.pagoTecnico.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- ========================================== -->
        <!-- VERSIÓN DESKTOP: TABLA -->
        <!-- ========================================== -->
        <div class="hidden md:block overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Orden</th>
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Fecha</th>
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Cliente</th>
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Equipo</th>
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Servicio</th>
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Monto</th>
                <th class="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Estado</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr
                v-for="orden in grupo.ordenes"
                :key="orden.idRecepcionEquipo"
                class="hover:bg-violet-50/30 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                @click="verOrden(orden)"
              >
                <td class="px-5 py-4 font-black text-violet-600 dark:text-violet-400 text-sm whitespace-nowrap">
                  <div class="flex items-center gap-1 group">
                    <Icon icon="mdi:eye" class="text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span class="hover:underline">#{{ orden.numeroOrden }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 whitespace-nowrap">
                  <div class="flex flex-col">
                    <span class="text-xs font-bold text-gray-500">ING: {{ formatFechaCorta(orden.fechaIngreso) }}</span>
                    <span v-if="orden.fechaReparacion" class="text-[10px] font-black text-violet-600 uppercase">REP: {{ formatFechaCorta(orden.fechaReparacion) }}</span>
                    <span v-if="orden.fechaEntrega" class="text-[10px] font-black text-emerald-600 uppercase">ENT: {{ formatFechaCorta(orden.fechaEntrega) }}</span>
                  </div>
                </td>
                <td class="px-5 py-4 font-bold text-gray-800 dark:text-gray-200 text-sm">
                  {{ orden.nombreCliente || '---' }}
                </td>
                <td class="px-5 py-4 text-sm text-gray-600 dark:text-gray-400">
                  {{ orden.descripcion || '---' }}
                </td>
                <td class="px-5 py-4 text-xs text-gray-500">
                  {{ orden.servicio || orden.falla || '---' }}
                </td>
                <td class="px-5 py-4 text-right font-black text-gray-800 dark:text-gray-200 text-sm">
                  ${{ (orden.precioTotalUsd || 0).toFixed(2) }}
                </td>
                <td class="px-5 py-4 text-center">
                  <span :class="getEstadoStyle(orden.nombreEstado)" class="inline-block px-3 py-1 text-[10px] font-black uppercase rounded-lg border shadow-sm">
                    {{ orden.nombreEstado || 'SIN ESTADO' }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50/80 dark:bg-gray-900/30 border-t-2 border-gray-200 dark:border-gray-700">
              <tr>
                <td colspan="6" class="px-5 py-3 text-right text-xs font-black text-gray-500 uppercase tracking-widest">
                  Total Generado: <span class="text-emerald-600 text-sm">${{ grupo.totalMonto.toFixed(2) }}</span> —
                  A Pagar ({{ grupo.porcentajeAplicado }}%): <span class="text-violet-600 text-sm">${{ grupo.pagoTecnico.toFixed(2) }}</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- ========================================== -->
        <!-- VERSIÓN MÓVIL: TARJETAS -->
        <!-- ========================================== -->
        <div class="md:hidden space-y-2 p-3">
          <div
            v-for="orden in grupo.ordenes"
            :key="orden.idRecepcionEquipo"
            class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-3 border border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-violet-50/30 dark:hover:bg-gray-700/30 transition-all"
            @click="verOrden(orden)"
          >
            <!-- Orden y Estado -->
            <div class="flex items-center justify-between mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div class="flex items-center gap-2">
                <span class="font-black text-violet-600 dark:text-violet-400 text-sm">#{{ orden.numeroOrden }}</span>
                <Icon icon="mdi:eye" class="text-gray-400 text-sm" />
              </div>
              <span :class="getEstadoStyle(orden.nombreEstado)" class="inline-block px-2 py-0.5 text-[8px] font-black uppercase rounded-lg border shadow-sm">
                {{ orden.nombreEstado || 'SIN ESTADO' }}
              </span>
            </div>

            <!-- Cliente y Equipo -->
            <div class="grid grid-cols-2 gap-1 mb-2 text-xs">
              <div>
                <span class="block text-[8px] font-black text-gray-400 uppercase tracking-widest">Cliente</span>
                <span class="font-bold text-gray-800 dark:text-white">{{ orden.nombreCliente || '---' }}</span>
              </div>
              <div>
                <span class="block text-[8px] font-black text-gray-400 uppercase tracking-widest">Equipo</span>
                <span class="font-bold text-gray-800 dark:text-white truncate block">{{ orden.descripcion || '---' }}</span>
              </div>
            </div>

            <!-- Fechas -->
            <div class="flex flex-wrap gap-2 mb-2 text-[10px]">
              <span class="font-bold text-gray-500">ING: {{ formatFechaCorta(orden.fechaIngreso) }}</span>
              <span v-if="orden.fechaReparacion" class="font-black text-violet-600">REP: {{ formatFechaCorta(orden.fechaReparacion) }}</span>
              <span v-if="orden.fechaEntrega" class="font-black text-emerald-600">ENT: {{ formatFechaCorta(orden.fechaEntrega) }}</span>
            </div>

            <!-- Monto -->
            <div class="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
              <span class="text-[8px] font-black text-gray-400 uppercase tracking-widest">Servicio</span>
              <span class="text-xs text-gray-500">{{ orden.servicio || orden.falla || '---' }}</span>
              <span class="text-sm font-black text-gray-800 dark:text-white">${{ (orden.precioTotalUsd || 0).toFixed(2) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';
import api from '@/api/recepcionEquipo.js';
import { obtenerTecnicos } from '@/api/tecnicos.js';

export default {
  name: 'ReporteTecnicoSemanal',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      cargando: false,
      recepciones: [],
      tecnicos: [],
      filtroTecnico: 'todos',
      offsetSemana: 0,
      porcentajeComision: 10,
      comisionesManuales: {},
    };
  },
  computed: {
    semanaActual() {
      const hoy = new Date();
      const diaSemana = hoy.getDay();
      const diffLunes = diaSemana === 0 ? -6 : 1 - diaSemana;
      const lunes = new Date(hoy);
      lunes.setDate(hoy.getDate() + diffLunes + (this.offsetSemana * 7));
      lunes.setHours(0, 0, 0, 0);
      const sabado = new Date(lunes);
      sabado.setDate(lunes.getDate() + 5);
      sabado.setHours(23, 59, 59, 999);
      return { lunes, sabado };
    },
    esSemanaActual() {
      return this.offsetSemana >= 0;
    },
    // SOLO FILTRA REPARADO Y ENTREGADO
    recepcionesCompletadas() {
      const { lunes, sabado } = this.semanaActual;
      let resultado = this.recepciones.filter(r => {
        // SOLO estados REPARADO o ENTREGADO
        const estado = (r.nombreEstado || '').toLowerCase();
        const esCompletada = estado.includes('reparado') || estado.includes('entrega') || estado.includes('entregado');
        if (!esCompletada) return false;

        const fechaRef = r.fechaReparacion || r.fechaEntrega || r.fechaIngreso;
        const fechaStr = String(fechaRef || '').slice(0, 10);
        if (!fechaStr) return false;
        const fecha = new Date(fechaStr + 'T00:00:00');
        return fecha >= lunes && fecha <= sabado;
      });
      
      resultado = resultado.filter(r =>
        r.nombreTecnicoReparador && r.nombreTecnicoReparador.trim() !== '' && r.nombreTecnicoReparador !== '---'
      );
      
      if (this.filtroTecnico !== 'todos') {
        resultado = resultado.filter(r => 
          r.idTecnicoReparador === this.filtroTecnico || 
          String(r.idTecnicoReparador) === String(this.filtroTecnico)
        );
      }
      return resultado;
    },
    gruposPorTecnico() {
      const agrupado = {};
      this.recepcionesCompletadas.forEach(r => {
        const tec = r.nombreTecnicoReparador || 'Sin Asignar';
        if (!agrupado[tec]) {
          agrupado[tec] = {
            tecnico: tec,
            ordenes: [],
            totalMonto: 0,
            pagoTecnico: 0
          };
        }
        agrupado[tec].ordenes.push(r);
        const monto = parseFloat(r.precioTotalUsd || 0);
        agrupado[tec].totalMonto += monto;
      });

      Object.values(agrupado).forEach(g => {
        const perc = this.comisionesManuales[g.tecnico] !== undefined 
          ? this.comisionesManuales[g.tecnico] 
          : this.porcentajeComision;
        g.porcentajeAplicado = perc;
        g.pagoTecnico = (g.totalMonto * perc) / 100;
      });

      return Object.values(agrupado).sort((a, b) => a.tecnico.localeCompare(b.tecnico));
    },
    hayDatos() {
      return this.gruposPorTecnico.length > 0;
    },
    resumenGlobal() {
      const totalReparaciones = this.recepcionesCompletadas.length;
      const montoTotal = this.recepcionesCompletadas.reduce((acc, r) => acc + parseFloat(r.precioTotalUsd || 0), 0);
      const pagoTotalTecnicos = this.gruposPorTecnico.reduce((acc, g) => acc + g.pagoTecnico, 0);

      return {
        totalReparaciones,
        montoTotal,
        pagoTotalTecnicos
      };
    }
  },
  async mounted() {
    await Promise.all([this.cargarTecnicos(), this.cargarDatos()]);
  },
  methods: {
    async cargarTecnicos() {
      try {
        const res = await obtenerTecnicos();
        if (res.success) this.tecnicos = res.data || [];
      } catch (e) { console.error('Error técnicos', e); }
    },
    async cargarDatos() {
      this.cargando = true;
      try {
        const res = await api.obtenerRecepciones();
        if (res.success) {
          this.recepciones = res.data || [];
        } else {
          this.toast.error('Error al cargar datos');
        }
      } catch (e) {
        this.toast.error('Error de conexión');
      } finally {
        this.cargando = false;
      }
    },
    semanaAnterior() { this.offsetSemana--; },
    semanaSiguiente() { if (!this.esSemanaActual) this.offsetSemana++; },
    irSemanaActual() { this.offsetSemana = 0; },
    formatFechaCorta(fecha) {
      if (!fecha) return '---';
      const d = new Date(fecha instanceof Date ? fecha : String(fecha).slice(0, 10) + 'T00:00:00');
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    iniciales(nombre) {
      if (!nombre) return '?';
      return nombre.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();
    },
    getEstadoStyle(estado) {
      const e = (estado || '').toLowerCase();
      if (e.includes('reparado') || e.includes('reparación')) return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
      if (e.includes('entrega') || e.includes('entregado')) return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-800';
    },
    verOrden(orden) {
      if (!orden || !orden.numeroOrden) return;
      this.$router.push({
        name: 'SeguimientoOrden',
        params: { id: orden.numeroOrden },
        query: { readOnly: 'true' }
      });
    },
    imprimirReporte() {
      const { lunes, sabado } = this.semanaActual;
      const titulo = `REPORTE TÉCNICO SEMANAL — ${this.formatFechaCorta(lunes)} al ${this.formatFechaCorta(sabado)}`;
      const self = this;
      const filas = this.gruposPorTecnico.map(grupo => `
        <div style="break-inside: avoid; margin-bottom: 28px;">
          <div style="background: linear-gradient(135deg, #7c3aed, #6d28d9); color: white; padding: 12px 16px; border-radius: 8px 8px 0 0; display: flex; justify-content: space-between; align-items: center;">
            <div>
              <strong style="font-size: 16px;">${grupo.tecnico}</strong>
              <div style="font-size: 11px; opacity: 0.8; margin-top: 2px;">Técnico Reparador</div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 14px; font-weight: 900; color: #10b981;">Total: $${grupo.totalMonto.toFixed(2)}</div>
              <div style="font-size: 14px; font-weight: 900;">A Pagar (${grupo.porcentajeAplicado}%): $${grupo.pagoTecnico.toFixed(2)}</div>
            </div>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #e5e7eb; border-top: none;">
            <thead>
              <tr style="background: #f3f4f6;">
                <th style="padding: 8px 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Orden</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Fecha</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Cliente</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Equipo</th>
                <th style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">Monto</th>
                <th style="padding: 8px 10px; text-align: center; border-bottom: 1px solid #e5e7eb;">Estado</th>
              </tr>
            </thead>
            <tbody>
              ${grupo.ordenes.map((o, i) => `
                <tr style="background: ${i % 2 === 0 ? '#fff' : '#faf9ff'};">
                  <td style="padding: 7px 10px; font-weight: bold; color: #7c3aed;">#${o.numeroOrden}</td>
                  <td style="padding: 7px 10px; color: #6b7280;">
                    <div>ING: ${self.formatFechaCorta(o.fechaIngreso)}</div>
                    ${o.fechaReparacion ? `<div style="color: #7c3aed; font-weight: bold; font-size: 9px;">REP: ${self.formatFechaCorta(o.fechaReparacion)}</div>` : ''}
                    ${o.fechaEntrega ? `<div style="color: #059669; font-weight: bold; font-size: 9px;">ENT: ${self.formatFechaCorta(o.fechaEntrega)}</div>` : ''}
                  </td>
                  <td style="padding: 7px 10px; font-weight: bold;">${o.nombreCliente || '---'}</td>
                  <td style="padding: 7px 10px;">${o.descripcion || '---'}</td>
                  <td style="padding: 7px 10px; text-align: right; font-weight: 900;">$${(o.precioTotalUsd || 0).toFixed(2)}</td>
                  <td style="padding: 7px 10px; text-align: center;"><span style="padding: 3px 10px; border-radius: 20px; background: #10b98120; color: #059669; font-size: 10px; font-weight: bold; text-transform: uppercase;">${o.nombreEstado || 'SIN ESTADO'}</span></td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="background: #f9fafb;">
                <td colspan="4" style="padding: 10px; text-align: right; font-weight: bold; text-transform: uppercase; font-size: 10px;">Total del Técnico:</td>
                <td style="padding: 10px; text-align: right; font-weight: 900; color: #059669; font-size: 12px;">$${grupo.totalMonto.toFixed(2)}</td>
                <td></td>
              </tr>
              <tr style="background: #f5f3ff;">
                <td colspan="4" style="padding: 10px; text-align: right; font-weight: bold; text-transform: uppercase; font-size: 10px; color: #7c3aed;">Pago Comisión (${grupo.porcentajeAplicado}%):</td>
                <td style="padding: 10px; text-align: right; font-weight: 900; color: #7c3aed; font-size: 12px;">$${grupo.pagoTecnico.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      `).join('');

      const ventana = window.open('', '_blank', 'width=1100,height=750');
      ventana.document.write(`<!DOCTYPE html><html><head>
        <meta charset="utf-8">
        <title>${titulo}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 24px; color: #111; background: #fff; }
          h1 { font-size: 20px; font-weight: 900; color: #4c1d95; margin: 0 0 4px; }
          .sub { font-size: 12px; color: #6b7280; margin: 0 0 24px; }
          @media print { @page { margin: 1.2cm; } }
        </style>
      </head><body>
        <h1>${titulo}</h1>
        <p class="sub">Jean Informático C.A. — Generado el ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        ${filas || '<p>No hay datos para imprimir.</p>'}
        <script>window.print(); window.onafterprint = () => window.close();<\/script>
      </body></html>`);
      ventana.document.close();
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }

.custom-scrollbar::-webkit-scrollbar { height: 4px; width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Estilos para pantallas extra pequeñas */
@media (max-width: 480px) {
  .xs\:block { display: block; }
}
@media (min-width: 481px) {
  .xs\:block { display: none; }
}
</style>